import React from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Tag, Card, Badge, Empty, message } from 'antd';
import { PlusOutlined, DeleteOutlined, CheckCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';

export default function Alerts() {
  const [alerts, setAlerts] = React.useState([]);
  const [devices, setDevices] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [form] = Form.useForm();

  const authHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  React.useEffect(() => {
    loadAlerts();
    loadDevices();
    const interval = setInterval(loadAlerts, 5000); // Actualizar cada 5s
    return () => clearInterval(interval);
  }, []);

  const loadDevices = async () => {
    try {
      const response = await axios.get('/api/devices', { headers: authHeader() });
      setDevices(response.data.devices || []);
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/alerts', { headers: authHeader() });
      setAlerts(response.data.alerts);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      await axios.post(`/api/alerts/${alertId}/acknowledge`, {}, { headers: authHeader() });
      loadAlerts();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleDelete = async (alertId) => {
    try {
      await axios.delete(`/api/alerts/${alertId}`, { headers: authHeader() });
      message.success('Alerta eliminada');
      loadAlerts();
    } catch (error) {
      message.error('No se pudo eliminar la alerta');
    }
  };

  const handleCreate = async (values) => {
    try {
      const condition = values.condition || 'gt';
      await axios.post('/api/alerts', {
        device_id: values.device_id,
        alert_type: values.alert_type,
        condition,
        threshold: values.threshold,
        sensor_name: values.sensor_name || ''
      }, { headers: authHeader() });

      message.success('Alerta creada');
      setModalVisible(false);
      form.resetFields();
      loadAlerts();
    } catch (error) {
      message.error('No se pudo crear la alerta');
    }
  };

  const columns = [
    {
      title: 'Dispositivo',
      dataIndex: 'device_name',
      key: 'device_name',
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Tipo de Alerta',
      dataIndex: 'alert_type',
      key: 'alert_type',
      render: (text) => <Tag color="orange">{text}</Tag>,
    },
    {
      title: 'Condición',
      dataIndex: 'condition',
      key: 'condition',
    },
    {
      title: 'Umbral',
      dataIndex: 'threshold',
      key: 'threshold',
      render: (val) => val ? <strong>{val}</strong> : '-',
    },
    {
      title: 'Estado',
      dataIndex: 'is_active',
      key: 'status',
      render: (active) => {
        const color = active ? 'red' : 'default';
        const text = active ? 'ACTIVA' : 'INACTIVA';
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Última Activación',
      dataIndex: 'last_triggered',
      key: 'last_triggered',
      render: (date) => date ? new Date(date).toLocaleString() : 'N/A',
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleAcknowledge(record.id)}
          >
            Reconocer
          </Button>
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Eliminar
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1 style={{ marginBottom: '20px' }}>🚨 Sistema de Alertas</h1>

      <Card>
        <div style={{ marginBottom: '16px' }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setModalVisible(true)}
            >
              Nueva Alerta
            </Button>
            <Button icon={<ReloadOutlined />} onClick={loadAlerts} loading={loading}>
              Actualizar
            </Button>
          </Space>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Badge count={alerts.filter(a => a.is_active).length} showZero>
            <span>Alertas Activas</span>
          </Badge>
        </div>

        {alerts.length === 0 ? (
          <Empty description="No hay alertas configuradas" />
        ) : (
          <Table
            dataSource={alerts}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: true }}
            rowClassName={(record) => record.is_active ? 'alert-row-active' : ''}
          />
        )}
      </Card>

      <Modal
        title="Nueva Alerta"
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={() => setModalVisible(false)}
      >
        <Form form={form} layout="vertical" onFinish={handleCreate}>
          <Form.Item
            name="device_id"
            label="Dispositivo"
            rules={[{ required: true }]}
          >
            <Select placeholder="Selecciona dispositivo">
              {devices.map((d) => (
                <Select.Option key={d.device_id} value={d.device_id}>{d.name}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="alert_type"
            label="Tipo de Alerta"
            rules={[{ required: true }]}
          >
            <Select placeholder="Selecciona tipo">
              <Select.Option value="temperature_high">Temperatura Alta</Select.Option>
              <Select.Option value="temperature_low">Temperatura Baja</Select.Option>
              <Select.Option value="humidity_high">Humedad Alta</Select.Option>
              <Select.Option value="humidity_low">Humedad Baja</Select.Option>
              <Select.Option value="offline">Dispositivo Desconectado</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="condition" label="Condición" initialValue="gt">
            <Select>
              <Select.Option value="gt">Mayor que</Select.Option>
              <Select.Option value="lt">Menor que</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="sensor_name" label="Sensor">
            <Input placeholder="temperatura / humedad" />
          </Form.Item>

          <Form.Item name="threshold" label="Umbral">
            <Input type="number" placeholder="30" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
